/**
 * Puzzle generation.
 *
 * A puzzle is only fair if a perfect answer provably exists, so the target is
 * not chosen and then reverse-engineered — it is *produced*, by running a
 * blend sequence and keeping the result. Replaying that sequence reproduces
 * the target exactly, which is both the guarantee and the test.
 *
 * Two problems make naive generation bad, and both are solved by aiming:
 *
 *  - Averaging in OKLab drifts toward the palette's centroid, which sits in
 *    warm mud. So every day *aims* at a desired hue, chroma and lightness,
 *    and keeps whichever of many candidate walks lands nearest.
 *  - The aim hue advances by the golden angle, so consecutive days never
 *    repeat a family and the wheel is covered evenly over any long run.
 */

import {
  Lab,
  Lch,
  deltaE,
  hueFamily,
  labToLch,
  maxChromaFor,
  mixLab,
  oklabToHex,
  relativeChroma,
} from './colour';
import { Pigment, Source, chooseBench, chooseBenchAimed, nameForLab } from './palette';
import { Rng, randInt, rngFrom } from './rng';
import { GEN_VERSION, Spec, dateForDayIndex, specForDay } from './schedule';

/** Each entry indexes the palette as it grows. Palette starts as the bench. */
export type Recipe = ReadonlyArray<readonly [number, number]>;

export interface Puzzle {
  /** Puzzle number, 1-based. */
  n: number;
  /** Day index since epoch. */
  index: number;
  date: string;
  weekday: number;
  spec: Spec;
  bench: Source[];
  target: { hex: string; name: string; family: string; lab: Lab };
  recipe: Recipe;
}

/**
 * Tuning dials. Defaults are what ships; `scripts/tune.ts` sweeps them.
 * Exposed because the right values are an empirical question about the
 * resulting puzzles, not something derivable.
 */
export interface GenOptions {
  /** Candidate walks tried per tolerance tier before widening. */
  candidatesPerTier: number;
  /** Aim score good enough to stop searching. */
  goodScore: number;
  /** Pigments pinned near the aim hue, keeping its family on the bench. */
  anchors: number;
  /** Whole sectors to push those anchors outward from the aim. */
  anchorShift: number;
  /** Reject a target this close (x tolerance) to a bare pigment. */
  pigmentWindow: number;
  /** Reject a target this close (x tolerance) to a single blend. */
  pairWindow: number;
  /** Reject a target duller than this, relative to its own hue. */
  minRelChroma: number;
  /** Multiplies every day's tolerance. The global difficulty dial. */
  toleranceScale: number;
  /** How hard the search chases the aim hue, versus chroma and lightness. */
  hueWeight: number;
  chromaWeight: number;
  lightnessWeight: number;
}

export const DEFAULT_GEN: GenOptions = {
  candidatesPerTier: 1800,
  goodScore: 13,
  anchors: 2,
  anchorShift: 0,
  pigmentWindow: 1.3,
  pairWindow: 0.85,
  minRelChroma: 0.26,
  toleranceScale: 1,
  hueWeight: 1,
  chromaWeight: 90,
  lightnessWeight: 70,
};
/** Tolerance multipliers tried in order. Slow growth: a stubborn day should
 *  end up marginally easier, not noticeably so. */
const WIDEN_TIERS = [1, 1.07, 1.14, 1.22] as const;
const MIN_TARGET_L = 0.17;
const MAX_TARGET_L = 0.9;
/** Golden angle: successive days never share a hue family. */
const HUE_STEP = 137.508;

type Built = Omit<Puzzle, 'n' | 'index' | 'date' | 'weekday'>;

interface Aim {
  h: number;
  L: number;
  relC: number;
}

export function benchSeedFor(index: number): string {
  return `PRISM|${GEN_VERSION}|${index}`;
}

export function generatePuzzle(index: number, opts: GenOptions = DEFAULT_GEN): Puzzle {
  const date = dateForDayIndex(index);
  const base = specForDay(index);
  const spec: Spec = opts.toleranceScale === 1
    ? base
    : { ...base, tolerance: base.tolerance * opts.toleranceScale };
  const built = attemptGenerate(index, spec, benchSeedFor(index), opts);

  return {
    ...built,
    n: index + 1,
    index,
    date: isoDate(date),
    weekday: date.getDay(),
  };
}

/** What today's target should feel like. Hue is deterministic; the rest is not. */
export function aimFor(index: number, rng: Rng): Aim {
  const h = (((index * HUE_STEP + rng() * 36 - 18) % 360) + 360) % 360;
  return {
    h,
    L: 0.36 + rng() * 0.44,
    relC: 0.55 + rng() * 0.35,
  };
}

/**
 * How far a candidate is from the aim. Hue dominates, so the wheel gets
 * covered; chroma is compared *relative* to what its own hue could reach, so
 * a vivid blue beats a dull yellow instead of always losing to it.
 */
function aimScore(candidate: Lab, aim: Aim, opts: GenOptions): number {
  const { L, C, h } = labToLch(candidate);
  let dh = Math.abs(h - aim.h) % 360;
  if (dh > 180) dh = 360 - dh;

  const relC = C / Math.max(0.02, maxChromaFor(L, h));
  return (
    dh * opts.hueWeight +
    Math.abs(relC - aim.relC) * opts.chromaWeight +
    Math.abs(L - aim.L) * opts.lightnessWeight
  );
}

function attemptGenerate(index: number, spec: Spec, seed: string, opts: GenOptions): Built {
  const rng = rngFrom(seed);
  const aim = aimFor(index, rng);

  for (const widen of WIDEN_TIERS) {
    const effective: Spec = { ...spec, tolerance: spec.tolerance * widen };
    let best: (Built & { score: number }) | null = null;

    for (let t = 0; t < opts.candidatesPerTier; t++) {
      // A fresh bench per candidate explores bench shape and walk together.
      const bench = toSources(
        chooseBenchAimed(rng, spec.pigments, aim.h, opts.anchors, opts.anchorShift),
      );
      const { target, recipe } = buildTarget(rng, bench, spec.blends);
      if (!passes(bench, target, effective, opts)) continue;

      const score = aimScore(target, aim, opts);
      if (!best || score < best.score) {
        best = { spec: effective, bench, recipe, target: describe(target), score };
      }
      if (best.score <= opts.goodScore) break;
    }

    if (best) {
      const { score, ...rest } = best;
      void score;
      return rest;
    }
  }

  // Last resort. Should be unreachable; a game that fails to load on
  // Christmas Day is worse than an easy puzzle.
  const relaxed: Spec = { ...spec, tolerance: spec.tolerance * 1.3 };
  const rng2 = rngFrom(seed + '|last');
  const bench = toSources(chooseBench(rng2, spec.pigments));
  const { target, recipe } = buildTarget(rng2, bench, spec.blends);
  return { spec: relaxed, bench, recipe, target: describe(target) };
}

function describe(lab: Lab): Built['target'] {
  return {
    hex: oklabToHex(lab),
    name: nameForLab(lab),
    family: hueFamily(lab),
    lab,
  };
}

function toSources(pigments: readonly Pigment[]): Source[] {
  return pigments.map((p) => ({ ...p, family: hueFamily(p.lab) }));
}

/**
 * Run `blends` mixes and return the final result plus the recipe.
 *
 * Pair choice is a small tournament on relative chroma. Pure uniform picking
 * averages its way to grey-brown over seven steps, which makes every hard day
 * look the same.
 */
function buildTarget(
  rng: Rng,
  bench: readonly Pigment[],
  blends: number,
): { target: Lab; recipe: Recipe } {
  const palette: Lab[] = bench.map((p) => p.lab);
  const recipe: Array<readonly [number, number]> = [];

  for (let k = 0; k < blends; k++) {
    const [i, j] = choosePair(rng, palette);
    palette.push(mixLab(palette[i], palette[j]));
    recipe.push([i, j]);
  }

  return { target: palette[palette.length - 1], recipe };
}

function choosePair(rng: Rng, palette: readonly Lab[]): [number, number] {
  let best: [number, number] = [0, 1];
  let bestScore = -Infinity;

  for (let r = 0; r < 3; r++) {
    const i = randInt(rng, 0, palette.length);
    let j = randInt(rng, 0, palette.length - 1);
    if (j >= i) j += 1;

    const score = relativeChroma(mixLab(palette[i], palette[j])) + rng() * 0.05;
    if (score > bestScore) {
      bestScore = score;
      best = [i, j];
    }
  }
  return best;
}

// ---------------------------------------------------------------------------
// Fairness invariants
// ---------------------------------------------------------------------------

export function passes(
  bench: readonly Pigment[],
  target: Lab,
  spec: Spec,
  opts: GenOptions = DEFAULT_GEN,
): boolean {
  const tol = spec.tolerance;

  // 1. Not reachable in zero blends. A target sitting on a pigment makes the
  //    day a formality.
  for (const p of bench) {
    if (deltaE(p.lab, target) < tol * opts.pigmentWindow) return false;
  }

  // 2. Not reachable in one blend, with room to spare. The radius sits well
  //    inside tolerance so the player can *tell* a single blend apart from
  //    the answer, rather than being fooled by a near miss.
  for (let i = 0; i < bench.length; i++) {
    for (let j = i + 1; j < bench.length; j++) {
      if (deltaE(mixLab(bench[i].lab, bench[j].lab), target) < tol * opts.pairWindow) {
        return false;
      }
    }
  }

  // 3. Not mud: still recognisably a colour, relative to its own hue.
  if (relativeChroma(target) < opts.minRelChroma) return false;

  // 4. Not so pale or so black that matching stops being about hue.
  const { L } = labToLch(target);
  if (L < MIN_TARGET_L || L > MAX_TARGET_L) return false;

  // 5. Every pigment must be a distinct decision.
  for (let i = 0; i < bench.length; i++) {
    for (let j = i + 1; j < bench.length; j++) {
      if (deltaE(bench[i].lab, bench[j].lab) < 8) return false;
    }
  }

  return true;
}

// ---------------------------------------------------------------------------
// Replay
// ---------------------------------------------------------------------------

/**
 * Replay a recipe against a bench, returning every palette state.
 *
 * The last entry is the final colour. This is the same code path a player
 * walks by hand, so if it reproduces the target, the puzzle is solvable.
 */
export function replay(bench: readonly Pigment[], recipe: Recipe): Lab[] {
  const palette: Lab[] = bench.map((p) => p.lab);
  for (const [i, j] of recipe) {
    if (i >= palette.length || j >= palette.length || i === j) {
      throw new Error(`bad recipe step [${i},${j}] on palette of ${palette.length}`);
    }
    palette.push(mixLab(palette[i], palette[j]));
  }
  return palette;
}

function isoDate(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

// ---------------------------------------------------------------------------
// Archive
// ---------------------------------------------------------------------------

export function generateRange(from: number, to: number, opts: GenOptions = DEFAULT_GEN): Puzzle[] {
  const out: Puzzle[] = [];
  for (let i = from; i <= to; i++) out.push(generatePuzzle(i, opts));
  return out;
}

export type { Lch };
